// The Highlights digest — the Dashboard's default view. A few computed insights
// about recent AI work (over a fixed window, see HL_DAYS below; scoped by
// state.scope, which for a lead/admin is switchable — see hlScope + the scope
// control),
// each a sentence with the real numbers + a "See the data →" drill-in into the
// surface behind it (with a grounding banner, see askbanner.ts). Rendered into
// #metric-detail when state.metric === 'highlights' (the landing default).
import { state, $, esc, usd, num, get, scopeQs } from './core'
import { openMetric, renderWindow, loadKpis } from './kpis'
import { onScopeChange, refreshFilterOptions } from './filterbar'
import { filterByArtifact, setView } from './sessions'
import { openArtifactSearch } from './artifacts'
import { renderAskBanner, clearAsked } from './askbanner'
import { storeStatus, noticeHtml } from './notice'
import { syncHash } from './router'

// Highlights runs over a fixed window — wider than the Dashboard's 7d default so
// short-lived signals still surface, but capped at 14 so the prior-period
// comparison (this 14d vs the 14d before it = 28d back) stays inside Claude
// Code's ~30-day session retention and still has data to compare against. 30d
// would reach 60 days back and the comparison would be empty. Change it here and
// the copy, the drill window, and the API call all follow.
var HL_DAYS = 14;
var HL_WIN = 'the last ' + HL_DAYS + ' days';
var HL_PREV = 'the previous ' + HL_DAYS + ' days';

// --- Whose work is this digest about? -----------------------------------------
//
// Highlights is scoped off the SAME state.scope as the Dashboard (see the scope
// control below), so the digest is no longer necessarily about the viewer — and
// its copy was written entirely in the first person ("You spent", "your AI
// spend"). Aggregate numbers under a "You" label are simply false, so every
// sentence takes its subject from here instead of hardcoding it.
//
// The `self` test is `!canPickUser`, NOT the role string: that's the same flag the
// filter bar keys its controls off, and it mirrors the server's resolveMetricScope
// (a member is pinned to their own id no matter what scope params they send). So a
// member's digest stays first-person even though state.scope.team holds their team.
function hlScope(): { mode: string; label: string } {
  var o = state.filterOpts;
  if (!o || !o.canPickUser) return { mode: 'self', label: '' };
  var u = state.scope.user;
  if (u === 'me') return { mode: 'self', label: '' };
  if (u) {
    // A third party, only reachable by picking someone in the Dashboard filter bar
    // and tabbing over. Name them rather than pretending it's the viewer.
    var hit = (o.users || []).filter(function (x) { return x.id === u; })[0];
    return { mode: 'user', label: (hit && (hit.name || hit.email)) || 'That user' };
  }
  if (state.scope.team) {
    var t = (o.teams || []).filter(function (x) { return x.id === state.scope.team; })[0];
    // 'unknown' rather than 'That team': the label is fed to teamPhrase, which
    // prepends an article, so a sentinel has to read as an adjective ("the unknown
    // team") not a demonstrative ("the That team"). Unreachable via the UI — the
    // team id only ever comes from a picker populated from this same list.
    return { mode: 'team', label: (t && t.name) || 'unknown' };
  }
  return { mode: 'org', label: 'The org' };
}

// The head's scope tail (" · DEV"), so a screenshot of the digest still says whose
// numbers these are. Empty for 'self' — the sentences already read "You …", and a
// member (who can never be anything but self) gets no scope furniture at all.
function scopeSuffix() {
  var s = hlScope();
  return s.mode === 'self' ? '' : ' · ' + esc(s.label);
}

// A team's name in PROSE. The bare name is ambiguous mid-sentence — "Platform's
// success rate" reads as a product's rate, not a group of people's — so prose says
// "the Platform team". Skipped when the name already carries the word ("Core Team",
// "Team Rocket"), which would otherwise double it. \b keeps "Teamwork" out.
// Only prose uses this: the toggle button and the masthead tail keep the bare name
// (position already says "team" there, and "Me · Platform team" is just longer).
function teamPhrase(label: string, cap?: boolean) {
  var art = cap ? 'The ' : 'the ';
  return art + esc(label) + (/\bteams?\b/i.test(label) ? '' : ' team');
}

// The digest's grammatical subject, sentence-initial: "You" / "The Platform team" /
// "The org" — the named modes both carry their article, so the sentences read the
// same shape whichever scope is active.
function subj() {
  var s = hlScope();
  if (s.mode === 'self') return 'You';
  if (s.mode === 'team') return teamPhrase(s.label, true);
  return esc(s.label);
}

// The possessive form. `cap` is for sentence-initial use; a bare person's name is a
// proper noun whose case is fixed either way ("Pat Kumar's").
function poss(cap?: boolean) {
  var s = hlScope();
  if (s.mode === 'self') return cap ? 'Your' : 'your';
  if (s.mode === 'org') return cap ? 'The org’s' : 'the org’s';
  if (s.mode === 'team') return teamPhrase(s.label, cap) + '’s';
  return esc(s.label) + '’s';
}

function base(p) { var a = String(p || '').split('/'); return a[a.length - 1] || p; }
function pct(n) { return Math.round(n) + '%'; }
function ratePct(r) { return Math.round((r || 0) * 100) + '%'; }
function signed(n) { return (Number(n) >= 0 ? '+' : '') + n + '%'; }
function stripTags(s) { return String(s || '').replace(/<[^>]+>/g, ''); }

// The change clause for a relative trend (spend, sessions). A big INCREASE (≥2×)
// switches from an unreadable percentage ("+2169%") to a multiple with the prior
// value for context ("≈23× the previous 14 days ($63.57)"); everything else (and
// any decrease, which is naturally bounded at −100%) stays a signed percentage.
// `fmt` formats the prior absolute value (usd for spend, num for counts).
function relChange(cur, prev, pctNum, fmt) {
  if (pctNum >= 100 && prev > 0) {
    var mult = cur / prev;
    var multStr = mult >= 10 ? String(Math.round(mult)) : String(Math.round(mult * 10) / 10);
    return '<b>≈' + multStr + '×</b> ' + HL_PREV + ' (' + esc(fmt(prev)) + ')';
  }
  return '<b>' + signed(pctNum) + '</b> vs ' + HL_PREV;
}

// A highlight that drills into the Dashboard pins its window to the Highlights
// window first, so the chart matches the statement the user just clicked (the
// Dashboard otherwise keeps whatever window it was last left on). This is why 14d
// has to be a selectable Dashboard window — the drill lands the user on it.
function dashHl() {
  state.days = HL_DAYS;
  setView('dashboard');
  renderWindow();
  loadKpis();
}

// Friendly noun for a walked facet, used in the orientation line + the tag labels.
var FACET_NOUN = { use_case: 'work type', repo: 'repo', model: 'model', harness: 'agent', complexity: 'complexity', autonomy: 'autonomy' };

// Per-tag-key plain-language hover tooltip, surfaced via the tag's title=.
// Deliberately subject-less ("the AI", not "your AI"): these define what a tag
// KIND means, and the digest is no longer necessarily about the viewer (see
// hlScope) — a possessive here would be wrong under a team or org lens.
var TAG_INFO = {
  file: { tip: 'A file the AI changed.' },
  PR: { tip: 'A pull request the AI opened or merged.' },
  feature: { tip: 'A unit of work these sessions are linked to.' },
  repo: { tip: 'The repository the work was in.' },
  model: { tip: 'The LLM model used.' },
  agent: { tip: 'The agent/harness used (Claude Code, Codex…).' },
  complexity: { tip: 'How hard the task was (LLM-judged).' },
  autonomy: { tip: 'How much the AI worked unaided (LLM-judged).' },
  'work type': { tip: 'The kind of work — implement, debug, research… (LLM-judged).' },
};

// A facet/artifact mention styled as a labeled tag — a small muted uppercase key
// ("PR", "FILE", "REPO") then the value with a faint underline — so a bare value
// isn't mistaken for prose. Numbers stay bold (<b>); the label + tooltip explain it.
function tag(key, value) {
  var info = TAG_INFO[key] || { tip: '' };
  return '<span class="hl-tag"' + (info.tip ? ' title="' + esc(info.tip) + '"' : '') +
    '><span class="hl-tag-k">' + esc(key) + '</span><span class="hl-tag-v">' + esc(value) + '</span></span>';
}

// Map an insight payload -> { html sentence, destination label, grounding `about`,
// the localStorage `section` its orientation is gated on, and run() = the drill-in }.
function present(h) {
  switch (h.kind) {
    case 'biggest_shipped': {
      var t = h.artifactKind === 'pr'
        ? tag('PR', h.repo && h.ident ? h.repo + '#' + h.ident + ' (' + h.title + ')' : h.title)
        : tag('feature', h.title);
      return {
        html: 'Most AI spend on shipped work: ' + t + ' — <b>' + esc(usd(h.cost)) + '</b>.',
        to: h.artifactKind === 'pr' ? 'Artifacts · PRs' : 'Artifacts · Features', section: 'artifacts',
        about: 'The Artifacts view lists every PR and feature the AI touched, with its block-attributed cost.',
        run: function () { openArtifactSearch(h.artifactKind, h.title); },
      };
    }
    case 'trend': {
      if (h.metric === 'rate') {
        var moved = (h.pp || 0) >= 0 ? 'rose' : 'fell';
        return {
          html: poss(true) + ' success rate ' + moved + ' from <b>' + ratePct(h.prev) + '</b> to <b>' + ratePct(h.cur) + '</b> vs ' + HL_PREV + '.',
          to: 'Session Outcome Rate', section: 'success_rate',
          about: 'The outcome rate over time — the same signal as the headline tile, here over ' + HL_WIN + '.',
          run: function () { dashHl(); state.sr.by = ''; openMetric('success_rate', true); },
        };
      }
      if (h.metric === 'sessions') {
        // The sessions detail section is gone (its headline tile made way for
        // code churn), so this drills into the Sessions list over the same window.
        return {
          html: subj() + ' ran <b>' + num(h.cur) + ' sessions</b> — ' + relChange(h.cur, h.prev, h.pct, num) + '.',
          to: 'Sessions', section: 'sessions',
          about: 'The Sessions list over ' + HL_WIN + ' — open one to see what it did.',
          run: function () { filterByArtifact('', '', HL_DAYS); },
        };
      }
      return {
        html: subj() + ' spent <b>' + esc(usd(h.cur)) + '</b> — ' + relChange(h.cur, h.prev, h.pct, usd) + '.',
        to: 'Total spend', section: 'total_spend',
        about: 'Spend over time — the same signal as the headline tile, here over ' + HL_WIN + '.',
        run: function () { dashHl(); state.spend.by = ''; openMetric('total_spend', true); },
      };
    }
    case 'stalled_spend': {
      var st = h.artifactKind === 'pr'
        ? tag('PR', h.repo && h.ident ? h.repo + '#' + h.ident + ' (' + h.title + ')' : h.title)
        : tag('feature', h.title);
      return {
        html: 'Most AI spend not yet shipped: ' + st + ' — <b>' + esc(usd(h.cost)) + '</b>.',
        to: h.artifactKind === 'pr' ? 'Artifacts · PRs' : 'Artifacts · Features', section: 'artifacts',
        about: 'The Artifacts view shows each PR and feature with its block-attributed cost and whether it has shipped yet.',
        run: function () { openArtifactSearch(h.artifactKind, h.title); },
      };
    }
    case 'converted_spend':
      return {
        html: '<b>' + pct(h.pct) + '</b> of ' + poss() + ' AI spend (<b>' + esc(usd(h.shipped)) + '</b> of ' + esc(usd(h.total)) + ') turned into shipped work.',
        to: 'Cost per shipped artifact', section: 'cost_artifact',
        about: 'The burn curve shades how much of each period’s spend converted into shipped work versus what’s still in flight.',
        run: function () { dashHl(); state.ca.kind = 'pr'; state.ca.userPicked = true; openMetric('cost_artifact', true); },
      };
    case 'active_file':
      return {
        html: subj() + ' touched ' + tag('file', base(h.path)) + ' in <b>' + num(h.sessions) + ' sessions</b> — more than any other.',
        to: 'Sessions · Files', section: 'sessions',
        about: 'This is the session list filtered to one file; open a session’s Files tab to see exactly what changed.',
        run: function () { filterByArtifact(h.path, 'file', HL_DAYS); },
      };
    case 'spend_concentration':
      return {
        html: '<b>' + pct(h.pct) + '</b> of ' + poss() + ' spend went to ' + tag(FACET_NOUN[h.facet] || h.facet, h.value) + '.',
        to: 'Total spend', section: 'total_spend',
        about: 'Spend over time, split by ' + (FACET_NOUN[h.facet] || h.facet) + ' — change the breakdown or filter to dig in.',
        run: function () { dashHl(); state.spend.by = h.facet; openMetric('total_spend', true); },
      };
    case 'success_spread': {
      var k = FACET_NOUN[h.facet] || h.facet;
      return {
        html: poss(true) + ' success rate is <b>' + ratePct(h.best.rate) + '</b> for ' + tag(k, h.best.value) + ' (' + num(h.best.n) +
          ' sessions) but <b>' + ratePct(h.worst.rate) + '</b> for ' + tag(k, h.worst.value) + ' (' + num(h.worst.n) + ').',
        to: 'Session Outcome Rate', section: 'success_rate',
        about: 'The outcome rate broken down by ' + k + ', so you can see where the AI does well.',
        run: function () { dashHl(); state.sr.by = h.facet; openMetric('success_rate', true); },
      };
    }
    case 'autonomy_complex': {
      var d = h.delta;
      var deltaStr = (d != null && d !== 0) ? ' — <b>' + (d > 0 ? 'up ' + d : 'down ' + Math.abs(d)) + '%</b> vs the prior ' + HL_DAYS + ' days' : '';
      // The sessions-over-time detail (which could break this down by autonomy)
      // is gone; land on the Sessions list over the same window instead — its
      // facet chips can narrow to complexity/autonomy from there.
      return {
        html: '<b>' + num(h.count) + ' of ' + poss() + ' sessions</b> ran autonomously on complex tasks' + deltaStr + '.',
        to: 'Sessions', section: 'sessions',
        about: 'The Sessions list over ' + HL_WIN + ' — filter by complexity and autonomy to see which tasks the agent takes on unaided.',
        run: function () { filterByArtifact('', '', HL_DAYS); },
      };
    }
    default:
      return null;
  }
}

// The most recent /api/highlights payload, kept so the tab can repaint (e.g. to
// add the store-state nudge) once the overview lands, without a refetch.
var lastHl: any = null;

// Highlights is its own tab, fixed to HL_DAYS (no window selector) but scoped by
// state.scope like every other metric surface — /api/highlights already threads
// the full resolved scope server-side (routes/dashboard.ts), clamps included, so
// forwarding scopeQs() is the whole of it. Clears the stale flag it's servicing.
export function renderHighlights() {
  state.hlStale = false;
  get('/api/highlights?days=' + HL_DAYS + scopeQs()).then(function (d) {
    lastHl = d;
    paintHighlights();
  });
}

// Refetch only if a scope change elsewhere invalidated the digest. Called on
// tab-in (the tab buttons + goHighlights), so a Dashboard scope change costs one
// request when the user actually comes to look, not one per twiddle.
export function refreshHighlightsIfStale() {
  if (state.hlStale) renderHighlights();
}

// --- The scope control ---------------------------------------------------------
//
// One control, not a copy of the Dashboard's filter bar: Highlights is an
// editorial digest, and a three-axis bar (Team/User/Repo) over eight prose
// sentences reads like a report builder. The single axis it offers is the one the
// prose is *about* — whose work is this. Repo narrowing stays a Dashboard concern.
//
// Per role, mirroring what the server would actually honor:
//   member    — nothing (resolveMetricScope pins them to themselves, so any
//               control here would be a lie); gated on canPickUser, the same flag
//               the filter bar uses.
//   team-lead — Me · <TeamName>. The team choice carries the team's NAME rather
//               than the word "Team": a lead has exactly one team, so "Team" is a
//               needless indirection, and the name doubles as the answer to
//               "which team am I lead of".
//   admin     — Me · Team · Org, plus a team <select> that appears ONLY while Team
//               is active. Three choices and an always-visible dropdown would be
//               two controls saying one thing.
//
// Styled as a TEXT toggle (no fill, no border, no pill), matching the .hrow-to
// "See the data →" buttons repeated down this column: nothing in the Highlights
// reading column carries a resting fill, and .hl-head is an editorial masthead
// (small-caps over an ink hairline) that a filled control fights. Deliberately NOT
// the .seg idiom — a segmented pill is dashboard-widget furniture, and .seg's
// emerald variant (.seg-primary) is reserved for a control that outranks its
// surroundings. A lens on the digest does not outrank the digest.
function scopeControlHtml(): string {
  var o = state.filterOpts;
  if (!o || !o.canPickUser) return '';
  var sc = hlScope();
  var isAdmin = o.role === 'admin';
  // `isName` marks a label that is a PROPER NOUN (a team name) rather than one of
  // the fixed words Me/Team/Org — it opts out of the head's inherited uppercasing,
  // which would render a team called "iOS Platform" as "IOS PLATFORM".
  var choice = function (mode: string, label: string, isName?: boolean) {
    return '<button type="button" class="hl-sc-b' + (isName ? ' hl-sc-name' : '') +
      (sc.mode === mode ? ' on' : '') + '" data-mode="' + mode + '">' + esc(label) + '</button>';
  };
  // The admin's team choice, once active, IS the picker: the word "Team" is
  // replaced by the team's name, and choosing a different one happens in place.
  // A <select> overlaid transparently on that text rather than shown as a box —
  // a native select sizes itself to its WIDEST option, so a bordered one would
  // leave "Dev" floating in space reserved for "Infrastructure Platform". The
  // overlay keeps the caret tight to the name and keeps the native control's
  // keyboard + screen-reader behaviour, which a div-and-popover would have to
  // reimplement. Not a <button> wrapper: a <select> inside a <button> is invalid.
  var namePicker = function (teams: any[]) {
    var opts = teams.map(function (t) {
      return '<option value="' + esc(t.id) + '"' + (state.scope.team === t.id ? ' selected' : '') + '>' + esc(t.name) + '</option>';
    }).join('');
    return '<span class="hl-sc-b hl-sc-name hl-sc-pick on">' + esc(sc.label) +
      '<span class="hl-sc-caret" aria-hidden="true">⌄</span>' +
      '<select id="hl-team" aria-label="Scope highlights to a team">' + opts + '</select></span>';
  };
  var btns = [choice('self', 'Me')];
  if (isAdmin) {
    var all = o.teams || [];
    // In team mode the name replaces the word. With 0–1 teams in the whole org
    // there is nothing to choose between, so it stays a plain choice — a select
    // holding one option is furniture pretending to be a decision.
    var teamChoice = sc.mode === 'team' && all.length > 1
      ? namePicker(all)
      : (sc.mode === 'team' && all.length === 1 ? choice('team', all[0].name, true) : choice('team', 'Team'));
    btns.push(teamChoice, choice('org', 'Org'));
  } else {
    // A lead's single team; `teams` is that one row (filterOptions).
    var mine = (o.teams || [])[0];
    if (!mine) return ''; // teamless lead — scopes like a member, nothing to pick
    btns.push(choice('team', mine.name, true));
  }
  // A third-party user scope (mode 'user') is reachable only from the Dashboard's
  // user select; it gets no choice of its own, so name who it is beside the toggle
  // and let any click here leave it.
  var note = sc.mode === 'user' ? '<span class="hl-sc-who">' + esc(sc.label) + '</span>' : '';
  // The separator is a real character in its own span (not a ::before), so it can
  // be muted independently of the choices it sits between.
  return '<span class="hl-scope">' + note +
    btns.join('<span class="hl-sc-sep">·</span>') + '</span>';
}

// Apply a segment click to the SHARED state.scope, then rescope both surfaces.
// Sharing scope with the Dashboard is deliberate: every highlight's "See the data
// →" drills into a Dashboard chart under a banner promising "the same signal as
// the headline tile". Two independent scopes would make that promise false —
// a lead reading "You spent $28.03" would land on a $132.68 chart.
function setHlScope(mode: string): void {
  var o = state.filterOpts;
  if (!o) return;
  // Repos are a Dashboard narrowing the digest doesn't offer; leaving a stale one
  // applied would silently narrow numbers this control claims are team-wide.
  state.scope.repos = [];
  if (mode === 'self') {
    state.scope.user = 'me';
    // Team is left as-is: with user='me' the user axis dominates either way, and
    // keeping it means clicking back to Team returns to the team you came from.
  } else if (mode === 'org') {
    state.scope.user = '';
    state.scope.team = '';
  } else {
    state.scope.user = '';
    // Entering Team mode needs a team: an admin arriving from Org has none, so
    // default to the first (which is what the picker will then show as selected).
    if (!state.scope.team) {
      var first = (o.teams || [])[0];
      state.scope.team = (first && first.id) || '';
    }
  }
  onScopeChange();        // keep the Dashboard's metrics agreeing with the digest
  refreshFilterOptions(); // and its repo/user option lists follow the new team
  // AFTER onScopeChange, which sets state.hlStale — renderHighlights clears it.
  // Reversed, every click here would leave the digest marked stale and refetch
  // again on the next tab-in.
  renderHighlights();     // repaint the digest (control + copy follow the scope)
}

// The OSS build pitched "the data lives in a local SQLite file, point your
// coding agent at it". That pitch does not survive the move to a shared server:
// there is no local store, and raw SQL access to everyone's sessions is exactly
// what the privacy model (PLAN.md §7) exists to prevent. Left as a seam rather
// than deleted outright — a scoped, policy-aware query surface (built on the
// policy module) is a plausible successor.
export function deepDiveHtml() {
  return '';
}

// Mirror the prompt at the bottom of the Dashboard tab. Hidden on an empty store
// (nothing to explore yet), matching how Highlights suppresses its own body.
export function paintDashAsk() {
  var el = $('#dash-ask');
  if (!el) return;
  el.innerHTML = storeStatus() === 'empty' ? '' : deepDiveHtml();
}

// Paint the digest from the last fetched payload. Separated from the fetch so the
// overview load can trigger a repaint that folds in the store-state nudge (a fresh
// store replaces the digest with a first-run prompt; an un-enriched one prepends
// the enrichment nudge). No-op until the first fetch resolves.
export function paintHighlights() {
  if (!lastHl) return;
  var winLabel = HL_WIN;
  // Fresh store (nothing analyzed): the digest would just say "nothing notable",
  // which reads as recent inactivity rather than an empty store — show the
  // first-run nudge alone instead.
  if (storeStatus() === 'empty') { $('#highlights').innerHTML = noticeHtml(); return; }
  var items = (lastHl && lastHl.highlights) || [];
  var rows = items.map(function (h, i) {
    var p = present(h);
    if (!p) return '';
    return '<div class="hrow">' +
      '<span class="hrow-q">' + p.html + '</span>' +
      '<button type="button" class="hrow-to" data-i="' + i + '">See the data <i>→</i></button></div>';
  }).join('');
  if (!rows) rows = '<div class="empty">Nothing notable in ' + esc(winLabel) + ' yet — widen the window, or run more sessions.</div>';
  $('#highlights').innerHTML =
    '<div class="hl">' +
    noticeHtml() + // enrichment nudge when un-enriched; '' once enrichment has run
    // The scope rides IN the head, so the digest's subject is legible even in a
    // screenshot — the sentences say "Dev spent…", the head says which lens that
    // is. '' for a member, whose scope isn't theirs to change.
    '<div class="hl-head">' +
      '<span class="hl-head-t">Notable in ' + esc(winLabel) + scopeSuffix() + '</span>' +
      scopeControlHtml() +
    '</div>' +
    '<div class="hlist">' + rows + '</div>' +
    // Order mirrors the top tabs (Metrics, Artifacts, Sessions).
    '<div class="see-tx-wrap">' +
      '<button class="see-tx" type="button" data-view="dashboard">Headline metrics →</button>' +
      '<button class="see-tx" type="button" data-view="artifacts">Artifacts →</button>' +
      // "Sessions", not "Your sessions": that tab is the org-wide list with its own
      // filters, so the possessive was already wrong for a lead/admin.
      '<button class="see-tx" type="button" data-view="sessions">Sessions →</button>' +
    '</div>' +
    deepDiveHtml() + '</div>';
  // Scope control: choices set the mode, the (admin-only, Team-mode-only) overlaid
  // select switches which team. Both go through setHlScope/state.scope so the
  // Dashboard follows — see the comment there on why the scope is shared.
  // `[data-mode]`, not `.hl-sc-b`: the active team name shares that class but is a
  // <span> wrapping the select, with no mode of its own. Binding it would put a
  // click handler under the select's own hit area — harmless with a guard, but the
  // guard is easy to lose. Selecting on the attribute makes the split structural.
  Array.prototype.forEach.call(document.querySelectorAll('#highlights .hl-sc-b[data-mode]'), function (b: HTMLElement) {
    b.onclick = function () { setHlScope(b.getAttribute('data-mode')!); };
  });
  var hlTeam = document.getElementById('hl-team') as HTMLSelectElement | null;
  if (hlTeam) hlTeam.onchange = function () {
    state.scope.team = hlTeam!.value;
    setHlScope('team');
  };
  Array.prototype.forEach.call(document.querySelectorAll('#highlights .see-tx[data-view]'), function (b) {
    b.onclick = function () { clearAsked(); setView(b.getAttribute('data-view')); window.scrollTo(0, 0); };
  });
  Array.prototype.forEach.call(document.querySelectorAll('#highlights .hrow-to'), function (el) {
    el.onclick = function () {
      var h = items[parseInt(el.getAttribute('data-i'), 10)];
      var p = present(h);
      if (!p) return;
      // Reuse the grounding banner: the insight IS the answer, so only the
      // orientation (`about`) shows — once per destination section.
      state.asked = { q: stripTags(p.html), answer: '', about: p.about, section: p.section };
      p.run();
      renderAskBanner(true); // renders the message in the slot above the chart + scrolls there
    };
  });
}

export function setInsightsSub(sub: 'highlights' | 'recommendations') {
  state.insightsSub = sub;
  var hl = document.getElementById('subview-highlights');
  var rec = document.getElementById('subview-recommendations');
  if (hl) hl.classList.toggle('on', sub === 'highlights');
  if (rec) rec.classList.toggle('on', sub === 'recommendations');
  Array.prototype.forEach.call(document.querySelectorAll('#insights-subtabs .subtab'), function (b) {
    b.classList.toggle('on', b.getAttribute('data-sub') === sub);
  });
  syncHash({ replace: true });
}

export function goHighlights() {
  clearAsked();
  setView('insights');
  setInsightsSub('highlights');
  renderHighlights();
}
